import { useEffect, useRef, useState, type FormEvent } from "react";

import type { ParentOverrideStateSnapshot, SkipReason } from "@vsta/contracts";

import type { FocusBlockConnectivity } from "./focus-block.js";

export type SkipUiCommand = {
  stepId: string;
  expectedRevision: number;
  expectedWorkStateRevision: number;
  reason: SkipReason;
  overrideId?: string;
};

type SkipOverrideScope = Extract<
  ParentOverrideStateSnapshot["overrides"][number]["scope"],
  { action: "skip-step" }
>;

export type OverrideDecisionUiCommand =
  | {
      overrideId: string;
      expectedRevision: number;
      intent: "approve" | "deny";
    }
  | {
      overrideId: string;
      expectedRevision: number;
      intent: "edit";
      scope: SkipOverrideScope;
      reason: SkipReason;
      expiresAt: string;
    };

export type OverrideCancellationUiCommand = {
  overrideId: string;
  expectedRevision: number;
  intent: "cancel";
};

export type SafeAlternative =
  ParentOverrideStateSnapshot["overrides"][number]["safeAlternatives"][number];

type ParentOverrideEditDraft = {
  overrideId: string;
  expectedRevision: number;
  scope: SkipOverrideScope;
  reasonCode: SkipReason["code"];
  detail: string;
  expiresAt: string;
};

const reasonLabels = {
  "unclear-directions": "Directions are unclear",
  "step-too-large": "Step feels too large",
  "technical-issue": "Technical issue",
  "schedule-conflict": "Schedule conflict",
  "fatigue-unwell": "Fatigue or feeling unwell",
  other: "Another reason",
  "end-of-day-closeout": "End-of-day closeout",
} as const;

function formatDateOnly(localDate: string) {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  }).format(new Date(`${localDate}T00:00:00.000Z`));
}

function closeDayDispositionLabel(
  disposition: Extract<
    ParentOverrideStateSnapshot["overrides"][number]["scope"],
    { action: "close-day-with-unresolved-must-finish" }
  >["reviewItems"][number]["disposition"],
) {
  if (disposition.kind === "replan") {
    return `Replan on ${formatDateOnly(disposition.targetLocalDate)}, position ${disposition.carryoverPosition}`;
  }
  if (disposition.kind === "defer") return "Defer for later planning";
  if (disposition.kind === "retain") return "Retain for parent review";
  return "Cancel by parent";
}

const safeAlternativeLabels = {
  continue: "Continue",
  "need-help": "Need Help",
  "report-problem": "Report a problem",
  "safety-exit": "Leave for safety",
} as const;

export function StudentSkipPanel({
  connectivity,
  stepId,
  expectedSchoolworkRevision,
  expectedWorkStateRevision,
  overrides,
  onSkip,
  onCancel,
  onSafeAlternative,
}: {
  connectivity: FocusBlockConnectivity;
  stepId: string;
  expectedSchoolworkRevision: number;
  expectedWorkStateRevision: number;
  overrides: ParentOverrideStateSnapshot;
  onSkip: (command: SkipUiCommand) => Promise<void>;
  onCancel?: (command: OverrideCancellationUiCommand) => Promise<void>;
  onSafeAlternative?: (alternative: SafeAlternative) => Promise<string | void>;
}) {
  const [pending, setPending] = useState(false);
  const [safePending, setSafePending] = useState<SafeAlternative | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedbackTitle, setFeedbackTitle] = useState("Skip update");
  const [supportPanel, setSupportPanel] = useState<
    "report-problem" | "safety-exit" | null
  >(null);
  const successFeedbackRef = useRef<HTMLHeadingElement>(null);
  const errorFeedbackRef = useRef<HTMLElement>(null);
  const supportHeadingRef = useRef<HTMLHeadingElement>(null);
  const supportInvokerRef = useRef<HTMLButtonElement>(null);
  const restoreSupportFocusRef = useRef(false);
  const current = overrides.overrides
    .filter(
      ({ scope }) => scope.action === "skip-step" && scope.stepId === stepId,
    )
    .at(-1);
  const safeAlternatives = current?.safeAlternatives ?? [
    "continue",
    "need-help",
    "report-problem",
    "safety-exit",
  ];

  useEffect(() => {
    if (error !== null) errorFeedbackRef.current?.focus();
    else if (message !== null) successFeedbackRef.current?.focus();
  }, [error, message]);

  useEffect(() => {
    if (supportPanel !== null) supportHeadingRef.current?.focus();
    else if (restoreSupportFocusRef.current) {
      restoreSupportFocusRef.current = false;
      supportInvokerRef.current?.focus();
    }
  }, [supportPanel]);

  function closeSupportPanel() {
    restoreSupportFocusRef.current = true;
    setSupportPanel(null);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const code = String(data.get("reason")) as SkipReason["code"];
    const detail = String(data.get("detail") ?? "").trim();
    setPending(true);
    setMessage(null);
    setError(null);
    setFeedbackTitle("Skip request sent");
    try {
      await onSkip({
        stepId,
        expectedRevision: expectedSchoolworkRevision,
        expectedWorkStateRevision,
        reason: { code, ...(detail === "" ? {} : { detail }) },
      });
      setMessage(
        "Skip requested. Nothing was marked Done or Skipped while you wait.",
      );
    } catch (reason) {
      setFeedbackTitle("Skip action needs attention");
      setError(
        reason instanceof Error
          ? reason.message
          : "Skip was not confirmed. Nothing changed.",
      );
    } finally {
      setPending(false);
    }
  }

  async function consume() {
    if (current?.scope.action !== "skip-step") return;
    setPending(true);
    setMessage(null);
    setError(null);
    setFeedbackTitle("Approved Skip used");
    try {
      await onSkip({
        stepId,
        expectedRevision: current.scope.expectedSchoolworkRevision,
        expectedWorkStateRevision: current.scope.expectedWorkStateRevision,
        reason: current.reason as SkipReason,
        overrideId: current.id,
      });
      setMessage(
        "This Step was Skipped with the one approved action. It remains visible for replan.",
      );
    } catch (reason) {
      setFeedbackTitle("Skip action needs attention");
      setError(
        reason instanceof Error
          ? reason.message
          : "The approved Skip was not confirmed. Refresh and review the decision.",
      );
    } finally {
      setPending(false);
    }
  }

  async function cancel() {
    if (current?.state !== "requested" || onCancel === undefined) return;
    setPending(true);
    setMessage(null);
    setError(null);
    setFeedbackTitle("Skip request cancelled");
    try {
      await onCancel({
        overrideId: current.id,
        expectedRevision: current.revision,
        intent: "cancel",
      });
      setMessage(
        "Skip request cancelled. This Step is still active and unfinished.",
      );
    } catch (reason) {
      setFeedbackTitle("Skip action needs attention");
      setError(
        reason instanceof Error
          ? reason.message
          : "The Skip request was not cancelled. Review the latest decision.",
      );
    } finally {
      setPending(false);
    }
  }

  async function useSafeAlternative(alternative: SafeAlternative) {
    if (alternative === "report-problem" || alternative === "safety-exit") {
      setSupportPanel(alternative);
      return;
    }
    if (onSafeAlternative === undefined) return;
    setSafePending(alternative);
    setMessage(null);
    setError(null);
    setFeedbackTitle("Safe choice ready");
    try {
      const nextMessage = await onSafeAlternative(alternative);
      setMessage(
        nextMessage ??
          `${safeAlternativeLabels[alternative]} remains available. The Skip request was not used.`,
      );
    } catch (reason) {
      setFeedbackTitle("Safe choice needs attention");
      setError(
        reason instanceof Error
          ? reason.message
          : `${safeAlternativeLabels[alternative]} could not be confirmed. The Skip request was not used.`,
      );
    } finally {
      setSafePending(null);
    }
  }

  return (
    <section className="override-panel" aria-labelledby={`skip-${stepId}`}>
      <p className="status-label">Enforce · protected plan change</p>
      <h3 id={`skip-${stepId}`}>Request Skip</h3>
      <p>
        A parent must approve this exact Skip. Requesting does not mark the Step
        Done or create Submission Evidence.
      </p>
      {current?.state === "requested" ? (
        <div className="override-state">
          <p>
            Waiting for a parent decision. You can keep working or use any safe
            choice below.
          </p>
          <p>
            <strong>Reason:</strong> {reasonLabels[current.reason.code]}
          </p>
          {onCancel === undefined ? null : (
            <button
              type="button"
              className="secondary-button"
              disabled={pending}
              onClick={() => void cancel()}
            >
              {pending ? "Cancelling request..." : "Cancel Skip request"}
            </button>
          )}
        </div>
      ) : current?.state === "approved" ? (
        <div className="override-state">
          <p>Parent approved this one exact Skip.</p>
          <button
            type="button"
            disabled={pending || connectivity !== "connected"}
            onClick={() => void consume()}
          >
            {pending
              ? "Applying approved Skip…"
              : "Use approval and Skip this Step"}
          </button>
        </div>
      ) : current?.state === "denied" ? (
        <p className="override-state">
          Parent denied this Skip. The Step is still active and safe choices
          remain available.
        </p>
      ) : current?.state === "consumed" ? (
        <p className="override-state">
          This approval has already been used. Live Step details are being
          refreshed.
        </p>
      ) : (
        <>
          {current?.state === "expired" ? (
            <p className="override-state">
              The earlier request expired without changing this Step. You can
              make a new request below.
            </p>
          ) : current?.state === "cancelled" ? (
            <p className="override-state">
              You cancelled the earlier request. The Step is still active; you
              can make a new request below.
            </p>
          ) : null}
          <form
            className="skip-request-form"
            onSubmit={(event) => void submit(event)}
          >
            <label htmlFor={`skip-reason-${stepId}`}>
              Why do you need to Skip?
            </label>
            <select
              id={`skip-reason-${stepId}`}
              name="reason"
              required
              defaultValue=""
              disabled={pending}
            >
              <option value="" disabled>
                Choose a reason
              </option>
              {Object.entries(reasonLabels)
                .filter(([code]) => code !== "end-of-day-closeout")
                .map(([code, label]) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
            </select>
            <label htmlFor={`skip-detail-${stepId}`}>Optional detail</label>
            <textarea
              id={`skip-detail-${stepId}`}
              name="detail"
              maxLength={500}
              rows={3}
              disabled={pending}
            />
            <button
              type="submit"
              disabled={pending || connectivity !== "connected"}
            >
              {pending ? "Requesting…" : "Request parent approval to Skip"}
            </button>
          </form>
        </>
      )}
      <div
        className="safe-alternatives"
        aria-label="Safe choices that remain available"
      >
        <p>
          <strong>Still available now</strong>
        </p>
        <ul
          className={
            onSafeAlternative === undefined ? undefined : "safe-action-list"
          }
        >
          {safeAlternatives.map((alternative) => (
            <li key={alternative}>
              {onSafeAlternative === undefined ? (
                safeAlternativeLabels[alternative]
              ) : (
                <button
                  type="button"
                  className="secondary-button"
                  disabled={safePending !== null}
                  onClick={(event) => {
                    if (
                      alternative === "report-problem" ||
                      alternative === "safety-exit"
                    ) {
                      supportInvokerRef.current = event.currentTarget;
                    }
                    void useSafeAlternative(alternative);
                  }}
                >
                  {safePending === alternative
                    ? `${safeAlternativeLabels[alternative]}...`
                    : safeAlternativeLabels[alternative]}
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>
      {supportPanel === "report-problem" ? (
        <section
          className="safe-support-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby={`report-problem-${stepId}`}
        >
          <h4
            id={`report-problem-${stepId}`}
            ref={supportHeadingRef}
            tabIndex={-1}
          >
            Report a problem safely
          </h4>
          <p>
            This problem note is not sent or saved in Release 0. Request Help
            now to notify the parent through the supported help flow, or contact
            a trusted adult using your usual phone or message method.
          </p>
          <div className="safe-support-actions">
            <button
              type="button"
              onClick={() => {
                setSupportPanel(null);
                void useSafeAlternative("need-help");
              }}
            >
              Request Help now
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={closeSupportPanel}
            >
              Close problem options
            </button>
          </div>
        </section>
      ) : supportPanel === "safety-exit" ? (
        <section
          className="safe-support-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby={`safety-exit-${stepId}`}
        >
          <h4
            id={`safety-exit-${stepId}`}
            ref={supportHeadingRef}
            tabIndex={-1}
          >
            Leave this schoolwork screen
          </h4>
          <p>
            You do not need Parent Override approval to leave for safety. Exit
            this screen, then contact a trusted adult or local emergency
            services when needed. Your Step and Skip request remain unchanged.
          </p>
          <div className="safe-support-actions">
            <a className="safety-exit-link" href="/login">
              Exit to sign-in
            </a>
            <button
              type="button"
              className="secondary-button"
              onClick={closeSupportPanel}
            >
              Stay on this screen
            </button>
          </div>
        </section>
      ) : null}
      {connectivity === "connected" ? null : (
        <p className="connectivity-note">
          Parent decisions need a connection. Continue, Need Help, reporting a
          problem, and leaving for safety are never blocked by this request.
        </p>
      )}
      {message === null && error === null ? null : (
        <section
          className="override-feedback"
          ref={error === null ? undefined : errorFeedbackRef}
          role={error === null ? "status" : "alert"}
          aria-live={error === null ? "polite" : "assertive"}
          tabIndex={error === null ? undefined : -1}
        >
          <h4
            ref={error === null ? successFeedbackRef : undefined}
            tabIndex={error === null ? -1 : undefined}
          >
            {feedbackTitle}
          </h4>
          <p>{error ?? message}</p>
        </section>
      )}
    </section>
  );
}

export function ParentOverrideDecisionPanel({
  connectivity,
  studentName,
  overrides,
  stepLabel,
  assignmentLabel,
  onDecision,
}: {
  connectivity: FocusBlockConnectivity;
  studentName: string;
  overrides: ParentOverrideStateSnapshot;
  stepLabel: (stepId: string) => string;
  assignmentLabel: (assignmentId: string) => string;
  onDecision: (command: OverrideDecisionUiCommand) => Promise<void>;
}) {
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedbackTitle, setFeedbackTitle] = useState("Parent Override updated");
  const [editDraft, setEditDraft] = useState<ParentOverrideEditDraft | null>(
    null,
  );
  const [reviewingEdit, setReviewingEdit] = useState(false);
  const successFeedbackRef = useRef<HTMLHeadingElement>(null);
  const errorFeedbackRef = useRef<HTMLElement>(null);
  const parentPanelHeadingRef = useRef<HTMLHeadingElement>(null);
  const editTriggerRef = useRef<HTMLButtonElement>(null);
  const editReviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const editReviewActionRef = useRef<HTMLButtonElement>(null);
  const restoreEditTriggerRef = useRef(false);
  const restoreEditControlRef = useRef(false);
  const skipOverrides = overrides.overrides.filter(
    ({ scope }) => scope.action === "skip-step",
  );
  const closeDayOverridesByExactRequest = new Map<
    string,
    ParentOverrideStateSnapshot["overrides"][number]
  >();
  for (const override of overrides.overrides) {
    if (override.scope.action !== "close-day-with-unresolved-must-finish") {
      continue;
    }
    const scope = override.scope;
    const key = [
      scope.dailyQueueId,
      scope.expectedQueueRevision,
      scope.expectedSchoolworkRevision,
      scope.expectedWorkStateRevision,
      scope.unresolvedDispositionDigest,
    ].join(":");
    const existing = closeDayOverridesByExactRequest.get(key);
    const candidateIsApproved = override.state === "approved";
    const existingIsApproved = existing?.state === "approved";
    if (
      existing === undefined ||
      (candidateIsApproved && !existingIsApproved) ||
      (candidateIsApproved === existingIsApproved &&
        override.requestedAt < existing.requestedAt)
    ) {
      closeDayOverridesByExactRequest.set(key, override);
    }
  }
  const closeDayOverrides = [...closeDayOverridesByExactRequest.values()];

  useEffect(() => {
    if (error !== null) errorFeedbackRef.current?.focus();
    else if (message !== null) successFeedbackRef.current?.focus();
  }, [error, message]);

  useEffect(() => {
    if (reviewingEdit) editReviewHeadingRef.current?.focus();
    else if (restoreEditControlRef.current) {
      restoreEditControlRef.current = false;
      editReviewActionRef.current?.focus();
    }
  }, [reviewingEdit]);

  useEffect(() => {
    if (editDraft === null && restoreEditTriggerRef.current) {
      restoreEditTriggerRef.current = false;
      (editTriggerRef.current ?? parentPanelHeadingRef.current)?.focus();
    }
  }, [editDraft]);

  async function decide(
    overrideId: string,
    expectedRevision: number,
    intent: "approve" | "deny",
  ) {
    setPendingId(overrideId);
    setMessage(null);
    setError(null);
    setFeedbackTitle("Parent Override updated");
    try {
      await onDecision({ overrideId, expectedRevision, intent });
      const closesDay = overrides.overrides.some(
        (override) =>
          override.id === overrideId &&
          override.scope.action === "close-day-with-unresolved-must-finish",
      );
      setMessage(
        intent === "approve"
          ? closesDay
            ? "Approved this one exact closeout. The student must still use it before expiration."
            : "Approved this one exact Skip. The student must still use it before expiration."
          : closesDay
            ? "Closeout denied. The Daily Queue and unfinished work remain open."
            : "Skip denied. The Step remains active and unfinished.",
      );
    } catch (reason) {
      setFeedbackTitle("Parent Override needs attention");
      setError(
        reason instanceof Error
          ? reason.message
          : "The Parent Override decision was not confirmed.",
      );
    } finally {
      setPendingId(null);
    }
  }

  function beginEdit(
    override: ParentOverrideStateSnapshot["overrides"][number],
    trigger: HTMLButtonElement,
  ) {
    if (override.scope.action !== "skip-step") return;
    const reason = override.reason as SkipReason;
    setEditDraft({
      overrideId: override.id,
      expectedRevision: override.revision,
      scope: override.scope,
      reasonCode: reason.code,
      detail: reason.detail ?? "",
      expiresAt: override.expiresAt,
    });
    setReviewingEdit(false);
    setMessage(null);
    setError(null);
    editTriggerRef.current = trigger;
  }

  function backToEditing() {
    restoreEditControlRef.current = true;
    setReviewingEdit(false);
  }

  function cancelEditing() {
    restoreEditTriggerRef.current = true;
    setEditDraft(null);
  }

  async function confirmEdit() {
    if (editDraft === null) return;
    setPendingId(editDraft.overrideId);
    setMessage(null);
    setError(null);
    setFeedbackTitle("Parent Override updated");
    try {
      await onDecision({
        overrideId: editDraft.overrideId,
        expectedRevision: editDraft.expectedRevision,
        intent: "edit",
        scope: editDraft.scope,
        reason: {
          code: editDraft.reasonCode,
          ...(editDraft.detail.trim() === ""
            ? {}
            : { detail: editDraft.detail.trim() }),
        },
        expiresAt: editDraft.expiresAt,
      });
      setMessage(
        "Changes saved. Review the exact Parent Override before approving or denying it.",
      );
      setEditDraft(null);
      setReviewingEdit(false);
    } catch (reason) {
      setFeedbackTitle("Parent Override needs attention");
      setError(
        reason instanceof Error
          ? reason.message
          : "The Parent Override changes were not confirmed.",
      );
    } finally {
      setPendingId(null);
    }
  }

  return (
    <section
      className="parent-override-panel"
      aria-labelledby="parent-overrides-title"
    >
      <p className="status-label">Protected plan changes</p>
      <h2 id="parent-overrides-title" ref={parentPanelHeadingRef} tabIndex={-1}>
        Parent Overrides
      </h2>
      {skipOverrides.length === 0 && closeDayOverrides.length === 0 ? (
        <p>No Parent Override requests yet.</p>
      ) : null}
      {skipOverrides.length === 0 ? null : (
        <ul className="override-list">
          {skipOverrides.map((override) => {
            const scope = override.scope;
            if (scope.action !== "skip-step") return null;
            return (
              <li key={override.id}>
                <div className="override-heading">
                  <h3>Skip this Step</h3>
                  <span>{override.state}</span>
                </div>
                <dl>
                  <dt>Student</dt>
                  <dd>{studentName}</dd>
                  <dt>Step</dt>
                  <dd>{stepLabel(scope.stepId)}</dd>
                  <dt>Assignment</dt>
                  <dd>{assignmentLabel(scope.assignmentId)}</dd>
                  <dt>Reason</dt>
                  <dd>
                    {reasonLabels[override.reason.code]}
                    {"detail" in override.reason && override.reason.detail
                      ? ` · ${override.reason.detail}`
                      : ""}
                  </dd>
                  <dt>Schedule effect</dt>
                  <dd>
                    The Step remains visible and unfinished for parent replan;
                    it does not count as Done or submitted.
                  </dd>
                  <dt>Exact scope</dt>
                  <dd>
                    Schoolwork revision {scope.expectedSchoolworkRevision} ·
                    Work State revision {scope.expectedWorkStateRevision}
                  </dd>
                  <dt>Expires</dt>
                  <dd>
                    <time dateTime={override.expiresAt}>
                      {new Date(override.expiresAt).toLocaleString()}
                    </time>
                  </dd>
                  <dt>Attribution</dt>
                  <dd>
                    Requested by Student
                    {override.decidedBy === null
                      ? " · waiting for Parent"
                      : ` · decided by Parent at ${new Date(override.decidedAt!).toLocaleString()}`}
                  </dd>
                </dl>
                {override.state === "requested" ? (
                  <div className="override-actions">
                    <button
                      type="button"
                      disabled={
                        pendingId !== null || connectivity !== "connected"
                      }
                      onClick={() =>
                        void decide(override.id, override.revision, "approve")
                      }
                    >
                      Approve this one Skip
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={
                        pendingId !== null || connectivity !== "connected"
                      }
                      onClick={() =>
                        void decide(override.id, override.revision, "deny")
                      }
                    >
                      Deny Skip
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={pendingId !== null}
                      onClick={(event) =>
                        beginEdit(override, event.currentTarget)
                      }
                    >
                      Edit Parent Override
                    </button>
                  </div>
                ) : null}
                {override.state === "requested" &&
                editDraft?.overrideId === override.id ? (
                  reviewingEdit ? (
                    <section
                      className="override-edit-review"
                      aria-labelledby={`edit-review-${override.id}`}
                    >
                      <h4
                        id={`edit-review-${override.id}`}
                        ref={editReviewHeadingRef}
                        tabIndex={-1}
                      >
                        Confirm these exact changes
                      </h4>
                      <p>
                        <strong>Protected action:</strong> Skip this Step only
                      </p>
                      <p>
                        <strong>Step:</strong> {stepLabel(scope.stepId)}
                      </p>
                      <p>
                        <strong>Assignment:</strong>{" "}
                        {assignmentLabel(scope.assignmentId)}
                      </p>
                      <p>
                        <strong>Reason:</strong>{" "}
                        {reasonLabels[editDraft.reasonCode]}
                        {editDraft.detail.trim() === ""
                          ? ""
                          : ` · ${editDraft.detail.trim()}`}
                      </p>
                      <p>
                        <strong>Exact scope:</strong> Schoolwork revision{" "}
                        {editDraft.scope.expectedSchoolworkRevision} · Work
                        State revision{" "}
                        {editDraft.scope.expectedWorkStateRevision}
                      </p>
                      <p>
                        <strong>Expires:</strong>{" "}
                        <time dateTime={editDraft.expiresAt}>
                          {new Date(editDraft.expiresAt).toLocaleString()}
                        </time>
                      </p>
                      <div className="override-actions">
                        <button
                          type="button"
                          disabled={
                            pendingId !== null || connectivity !== "connected"
                          }
                          onClick={() => void confirmEdit()}
                        >
                          {pendingId === override.id
                            ? "Saving changes..."
                            : "Confirm changes"}
                        </button>
                        <button
                          type="button"
                          className="secondary-button"
                          disabled={pendingId !== null}
                          onClick={backToEditing}
                        >
                          Back to editing
                        </button>
                      </div>
                      {connectivity === "connected" ? null : (
                        <p className="connectivity-note">
                          Reconnect to confirm. Your reviewed changes remain on
                          this screen.
                        </p>
                      )}
                    </section>
                  ) : (
                    <form
                      className="override-edit-form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        setReviewingEdit(true);
                      }}
                    >
                      <h4>Edit this Skip request</h4>
                      <p>
                        The protected action, Step, and Assignment stay fixed.
                        Review every change before confirming it.
                      </p>
                      <label htmlFor={`edit-reason-${override.id}`}>
                        Reason for this Skip
                      </label>
                      <select
                        id={`edit-reason-${override.id}`}
                        value={editDraft.reasonCode}
                        onChange={(event) =>
                          setEditDraft((current) =>
                            current === null
                              ? null
                              : {
                                  ...current,
                                  reasonCode: event.target
                                    .value as SkipReason["code"],
                                },
                          )
                        }
                      >
                        {Object.entries(reasonLabels)
                          .filter(([code]) => code !== "end-of-day-closeout")
                          .map(([code, label]) => (
                            <option key={code} value={code}>
                              {label}
                            </option>
                          ))}
                      </select>
                      <label htmlFor={`edit-detail-${override.id}`}>
                        Optional detail
                      </label>
                      <textarea
                        id={`edit-detail-${override.id}`}
                        value={editDraft.detail}
                        maxLength={500}
                        rows={3}
                        onChange={(event) =>
                          setEditDraft((current) =>
                            current === null
                              ? null
                              : { ...current, detail: event.target.value },
                          )
                        }
                      />
                      <label htmlFor={`edit-schoolwork-${override.id}`}>
                        Schoolwork revision
                      </label>
                      <input
                        id={`edit-schoolwork-${override.id}`}
                        type="number"
                        min={1}
                        step={1}
                        value={editDraft.scope.expectedSchoolworkRevision}
                        onChange={(event) =>
                          setEditDraft((current) =>
                            current === null
                              ? null
                              : {
                                  ...current,
                                  scope: {
                                    ...current.scope,
                                    expectedSchoolworkRevision: Number(
                                      event.target.value,
                                    ),
                                  },
                                },
                          )
                        }
                      />
                      <label htmlFor={`edit-work-state-${override.id}`}>
                        Work State revision
                      </label>
                      <input
                        id={`edit-work-state-${override.id}`}
                        type="number"
                        min={1}
                        step={1}
                        value={editDraft.scope.expectedWorkStateRevision}
                        onChange={(event) =>
                          setEditDraft((current) =>
                            current === null
                              ? null
                              : {
                                  ...current,
                                  scope: {
                                    ...current.scope,
                                    expectedWorkStateRevision: Number(
                                      event.target.value,
                                    ),
                                  },
                                },
                          )
                        }
                      />
                      <label htmlFor={`edit-expiration-${override.id}`}>
                        Expiration (UTC)
                      </label>
                      <input
                        id={`edit-expiration-${override.id}`}
                        type="datetime-local"
                        value={editDraft.expiresAt.slice(0, 16)}
                        onChange={(event) => {
                          const next = new Date(
                            `${event.target.value}:00.000Z`,
                          );
                          if (Number.isNaN(next.getTime())) return;
                          setEditDraft((current) =>
                            current === null
                              ? null
                              : { ...current, expiresAt: next.toISOString() },
                          );
                        }}
                      />
                      <div className="override-actions">
                        <button type="submit" ref={editReviewActionRef}>
                          Review Parent Override changes
                        </button>
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={cancelEditing}
                        >
                          Cancel editing
                        </button>
                      </div>
                    </form>
                  )
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {closeDayOverrides.length === 0 ? null : (
        <ul className="override-list">
          {closeDayOverrides.map((override) => {
            const scope = override.scope;
            if (scope.action !== "close-day-with-unresolved-must-finish") {
              return null;
            }
            return (
              <li key={override.id}>
                <div className="override-heading">
                  <h3>Close Daily Queue with unresolved must-finish work</h3>
                  <span>{override.state}</span>
                </div>
                <dl>
                  <dt>Student</dt>
                  <dd>{studentName}</dd>
                  <dt>Reason</dt>
                  <dd>End-of-day closeout</dd>
                  <dt>Schedule effect</dt>
                  <dd>
                    Every unfinished Queue Item remains factual and keeps its
                    reviewed replan, defer, retain, or parent-cancel decision.
                  </dd>
                  <dt>Reviewed unfinished work</dt>
                  <dd>
                    <ol className="closeout-review-items">
                      {scope.reviewItems.map((item) => (
                        <li key={item.queueItemId}>
                          <strong>{stepLabel(item.stepId)}</strong>
                          <span>{assignmentLabel(item.assignmentId)}</span>
                          <span>
                            {item.mustFinish ? "Must finish · " : ""}
                            {closeDayDispositionLabel(item.disposition)}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </dd>
                  <dt>Next planned Step</dt>
                  <dd>
                    {scope.proposedNextStep === null
                      ? "No future first Step is proposed."
                      : `Next planned Step is ${stepLabel(scope.proposedNextStep.stepId)} on ${formatDateOnly(scope.proposedNextStep.targetLocalDate)}, position ${scope.proposedNextStep.carryoverPosition}.`}
                  </dd>
                  <dt>Exact scope</dt>
                  <dd>
                    Daily Queue revision {scope.expectedQueueRevision} ·
                    Schoolwork revision {scope.expectedSchoolworkRevision} ·
                    Work State revision {scope.expectedWorkStateRevision}
                  </dd>
                  <dt>Expires</dt>
                  <dd>
                    <time dateTime={override.expiresAt}>
                      {new Date(override.expiresAt).toLocaleString()}
                    </time>
                  </dd>
                  <dt>Attribution</dt>
                  <dd>
                    Requested by{" "}
                    {override.requestedBy.role === "parent"
                      ? "Parent"
                      : "Student"}
                    {override.decidedBy === null
                      ? " · waiting for Parent"
                      : ` · decided by Parent at ${new Date(override.decidedAt!).toLocaleString()}`}
                  </dd>
                </dl>
                {override.state === "requested" ? (
                  <div className="override-actions">
                    <button
                      type="button"
                      disabled={
                        pendingId !== null || connectivity !== "connected"
                      }
                      onClick={() =>
                        void decide(override.id, override.revision, "approve")
                      }
                    >
                      Approve this one closeout
                    </button>
                    <button
                      type="button"
                      className="secondary-button"
                      disabled={
                        pendingId !== null || connectivity !== "connected"
                      }
                      onClick={() =>
                        void decide(override.id, override.revision, "deny")
                      }
                    >
                      Deny closeout
                    </button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {message === null && error === null ? null : (
        <section
          className="override-feedback"
          ref={error === null ? undefined : errorFeedbackRef}
          role={error === null ? "status" : "alert"}
          aria-live={error === null ? "polite" : "assertive"}
          tabIndex={error === null ? undefined : -1}
        >
          <h3
            ref={error === null ? successFeedbackRef : undefined}
            tabIndex={error === null ? -1 : undefined}
          >
            {feedbackTitle}
          </h3>
          <p>{error ?? message}</p>
        </section>
      )}
    </section>
  );
}
